/**
 * Maintenance Page Component
 * Displayed when the system is in maintenance mode
 * Auto-redirects after 5 seconds OR when maintenance is turned off
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coffee, Clock, ArrowLeft } from 'lucide-react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface MaintenancePageProps {
  redirectPath?: string;
  countdown?: number; // seconds
}

const MaintenancePage: React.FC<MaintenancePageProps> = ({
  redirectPath = '/',
  countdown = 5
}) => {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(countdown);

  // Listen for maintenance mode changes and balance - auto redirect when resolved
  useEffect(() => {
    console.log('[MaintenancePage] Setting up real-time listener for auto-redirect...');

    let isRedirecting = false; // Prevent multiple redirects

    // Check if conditions are resolved (maintenance OFF AND balance sufficient)
    const checkAndRedirect = async (manualMaintenanceEnabled: boolean) => {
      if (isRedirecting) return;

      // Check balance
      let balanceLow = false;
      try {
        const response = await fetch(`${import.meta.env.VITE_MODAL_API_BASE_URL}/admin/billing/balance`);
        if (response.ok) {
          const data = await response.json();
          const currentBalance = data.balance || 0;
          balanceLow = currentBalance < 1200;
          console.log(`[MaintenancePage] Balance check: ${currentBalance} VND (low: ${balanceLow})`);
        }
      } catch (error) {
        console.error('[MaintenancePage] Error checking balance:', error);
      }

      // If BOTH are resolved (manual OFF AND balance sufficient), redirect immediately
      if (!manualMaintenanceEnabled && !balanceLow) {
        console.log('✅ [MaintenancePage] Maintenance resolved! Redirecting to home...');
        isRedirecting = true;
        // Force reload to ensure fresh state
        window.location.href = redirectPath;
      }
    };

    // Listen for maintenance mode changes
    const unsubscribe = onSnapshot(
      doc(db, 'system_settings', 'maintenance_mode'),
      (docSnap) => {
        const manualMaintenanceEnabled = docSnap.exists() ? (docSnap.data().enabled || false) : false;
        console.log(`[MaintenancePage] Maintenance mode: ${manualMaintenanceEnabled ? 'ON' : 'OFF'}`);
        checkAndRedirect(manualMaintenanceEnabled);
      }
    );

    // Also check balance periodically (every 60 seconds)
    const balanceInterval = setInterval(async () => {
      // Get current maintenance status
      try {
        const docRef = doc(db, 'system_settings', 'maintenance_mode');
        const docSnap = await getDoc(docRef);
        const manualMaintenanceEnabled = docSnap.exists() ? (docSnap.data().enabled || false) : false;
        await checkAndRedirect(manualMaintenanceEnabled);
      } catch (error) {
        console.error('[MaintenancePage] Error checking status:', error);
      }
    }, 60000);

    return () => {
      unsubscribe();
      clearInterval(balanceInterval);
    };
  }, [redirectPath]);

  useEffect(() => {
    // Countdown timer
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Do NOT auto-redirect here to prevent loops
          // Only redirect when resolved (handled by checkAndRedirect)
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, redirectPath]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Coffee Beans */}
        <div className="absolute top-20 left-10 w-4 h-4 bg-amber-400 rounded-full opacity-20 animate-float"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-orange-400 rounded-full opacity-20 animate-float-delayed"></div>
        <div className="absolute bottom-32 left-1/4 w-5 h-5 bg-yellow-400 rounded-full opacity-20 animate-float"></div>
        <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-amber-300 rounded-full opacity-20 animate-float-delayed"></div>

        {/* Decorative Circles */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-gradient-to-br from-amber-200 to-transparent rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-gradient-to-tl from-orange-200 to-transparent rounded-full opacity-30 blur-3xl"></div>
      </div>

      <div className="max-w-2xl w-full text-center relative z-10">
        {/* Illustration - Coffee Break SVG */}
        <div className="mb-8 relative">
          <div className="inline-block relative animate-float-slow">{/* Coffee Break Illustration SVG */}
            <svg viewBox="0 0 500 400" className="w-full max-w-md mx-auto drop-shadow-2xl" xmlns="http://www.w3.org/2000/svg">
              {/* Background Circle */}
              <circle cx="250" cy="200" r="180" fill="#FEF3C7" opacity="0.5" />

              {/* Coffee Cup */}
              <g transform="translate(200, 150)">
                {/* Cup Body */}
                <path d="M 20 30 Q 20 80 40 100 L 60 100 Q 80 80 80 30 Z" fill="#FBBF24" stroke="#92400E" strokeWidth="2" />
                {/* Cup Handle */}
                <path d="M 80 50 Q 100 50 100 70 Q 100 90 80 90" fill="none" stroke="#92400E" strokeWidth="2" />
                {/* Coffee Surface */}
                <ellipse cx="50" cy="30" rx="30" ry="8" fill="#78350F" />
                {/* Steam */}
                <path d="M 35 20 Q 35 10 40 5" stroke="#D97706" strokeWidth="2" fill="none" opacity="0.7" className="animate-pulse">
                  <animate attributeName="opacity" values="0.3;0.7;0.3" dur="2s" repeatCount="indefinite" />
                </path>
                <path d="M 50 15 Q 50 5 55 0" stroke="#D97706" strokeWidth="2" fill="none" opacity="0.5" className="animate-pulse">
                  <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2.5s" repeatCount="indefinite" />
                </path>
                <path d="M 65 20 Q 65 10 70 5" stroke="#D97706" strokeWidth="2" fill="none" opacity="0.6" className="animate-pulse">
                  <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1.8s" repeatCount="indefinite" />
                </path>
              </g>

              {/* Sleeping Person */}
              <g transform="translate(100, 200)">
                {/* Head */}
                <circle cx="50" cy="40" r="25" fill="#FBBF24" />
                {/* Body */}
                <ellipse cx="50" cy="80" rx="35" ry="30" fill="#F59E0B" />
                {/* Closed Eyes */}
                <line x1="40" y1="40" x2="48" y2="40" stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
                <line x1="52" y1="40" x2="60" y2="40" stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
                {/* Smile */}
                <path d="M 40 50 Q 50 55 60 50" stroke="#92400E" strokeWidth="2" fill="none" strokeLinecap="round" />
                {/* Z Z Z (Sleep) */}
                <text x="70" y="20" fill="#D97706" fontSize="20" fontWeight="bold" opacity="0.8">
                  Z
                  <animate attributeName="opacity" values="0.3;0.8;0.3" dur="3s" repeatCount="indefinite" />
                </text>
                <text x="85" y="10" fill="#D97706" fontSize="16" fontWeight="bold" opacity="0.6">
                  Z
                  <animate attributeName="opacity" values="0.2;0.6;0.2" dur="3s" begin="0.5s" repeatCount="indefinite" />
                </text>
                <text x="95" y="0" fill="#D97706" fontSize="12" fontWeight="bold" opacity="0.4">
                  Z
                  <animate attributeName="opacity" values="0.1;0.4;0.1" dur="3s" begin="1s" repeatCount="indefinite" />
                </text>
              </g>

              {/* Pillow */}
              <ellipse cx="350" cy="280" rx="60" ry="30" fill="#FCD34D" opacity="0.7" />

              {/* Coffee Beans Decoration */}
              <g opacity="0.4">
                <ellipse cx="120" cy="100" rx="8" ry="12" fill="#92400E" transform="rotate(20 120 100)" />
                <ellipse cx="380" cy="120" rx="8" ry="12" fill="#92400E" transform="rotate(-30 380 120)" />
                <ellipse cx="400" cy="300" rx="8" ry="12" fill="#92400E" transform="rotate(45 400 300)" />
              </g>

              {/* Moon */}
              <circle cx="80" cy="80" r="20" fill="#FDE68A" opacity="0.8" />
              <circle cx="85" cy="75" r="18" fill="#FEF3C7" opacity="0.8" />
            </svg>
          </div>

          {/* Floating Coffee Icon */}
          <div className="absolute top-0 right-0 animate-bounce-slow">
            <Coffee className="w-12 h-12 text-amber-600 opacity-40" />
          </div>
        </div>

        {/* Main Message */}
        <div className="mb-8 space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg border border-amber-200">
            <Coffee className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Coffee Break Time!</span>
          </div>

          <p className="text-lg sm:text-xl text-gray-700 font-medium mb-2 mt-6">
            Trang thiết kế tạm thời không thể hoạt động
          </p>

          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Chúng mình đang nạp năng lượng để phục vụ bạn tốt hơn. Vui lòng quay lại sau nhé! 🌟
          </p>
        </div>

        {/* Countdown Card */}
        <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl border-2 border-amber-200/50 p-8 mb-6 transform hover:scale-105 transition-transform">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Clock className="w-6 h-6 text-amber-600 animate-pulse" />
            <p className="text-sm font-bold text-gray-700 uppercase tracking-wide">
              Tự động chuyển hướng sau
            </p>
          </div>

          <div className="relative">
            {/* Countdown Number with Glow Effect */}
            <div className="relative inline-block">
              <div className="text-7xl sm:text-8xl font-black bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-500 bg-clip-text text-transparent mb-4 tabular-nums drop-shadow-lg animate-pulse-slow">
                {secondsLeft}
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 text-7xl sm:text-8xl font-black text-amber-400 blur-2xl opacity-30 animate-pulse">
                {secondsLeft}
              </div>
            </div>

            {/* Circular Progress */}
            <div className="flex justify-center mb-4">
              <svg className="w-48 h-12" viewBox="0 0 200 50">
                {/* Background */}
                <rect
                  x="10"
                  y="15"
                  width="180"
                  height="20"
                  rx="10"
                  fill="#FEF3C7"
                  stroke="#F59E0B"
                  strokeWidth="2"
                />
                {/* Progress Fill with Gradient */}
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: '#F59E0B', stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: '#FBBF24', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#FCD34D', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <rect
                  x="10"
                  y="15"
                  width={180 * (secondsLeft / countdown)}
                  height="20"
                  rx="10"
                  fill="url(#progressGradient)"
                  className="transition-all duration-1000 ease-linear"
                >
                  <animate attributeName="opacity" values="0.8;1;0.8" dur="1s" repeatCount="indefinite" />
                </rect>
                {/* Shine Effect */}
                <rect
                  x="10"
                  y="15"
                  width={180 * (secondsLeft / countdown)}
                  height="10"
                  rx="10"
                  fill="white"
                  opacity="0.3"
                />
              </svg>
            </div>

            <p className="text-sm font-bold text-amber-700 tracking-wider">GIÂY</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {/* Manual Redirect Button */}
          <button
            onClick={() => navigate(redirectPath)}
            className="group px-8 py-4 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 text-white font-bold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-110 active:scale-95 transition-all duration-300 flex items-center gap-3"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Quay lại ngay</span>
          </button>
        </div>

        {/* Decorative Elements - Coffee Beans */}
        <div className="mt-12 flex justify-center gap-3">
          <div className="w-3 h-3 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
          <div className="w-3 h-3 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-3 h-3 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-3 h-3 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-30px); }
        }

        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }

        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }

        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float-delayed 4s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default MaintenancePage;
